
import React, { useState, useEffect } from 'react';
import { flashSaleService } from '../services/flashSaleService';
import { storage } from '../lib/storage';
import { FlashSale, Product } from '../types';
import { 
  Zap, 
  Plus, 
  Trash2, 
  Clock, 
  Calendar, 
  Tag, 
  Package, 
  Loader2, 
  X, 
  Check, 
  AlertCircle,
  TrendingUp,
  Sparkles,
  Timer
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminFlashSales: React.FC<Props> = ({ storeId }) => {
  const [sales, setSales] = useState<FlashSale[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    discountPercentage: 10,
    startTime: '',
    endTime: '',
    productIds: [] as string[]
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setIsLoading(true);
    setSales(flashSaleService.getStoreFlashSales(storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
    setIsLoading(false);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.productIds.length === 0) return alert("Bhai, kam se kam ek product toh select karo!");
    
    flashSaleService.createSale({
      ...formData,
      storeId,
      status: 'SCHEDULED'
    });
    
    setShowModal(false);
    loadData();
    setFormData({ name: '', discountPercentage: 10, startTime: '', endTime: '', productIds: [] });
  };

  const toggleProduct = (id: string) => {
    setFormData(prev => ({
      ...prev,
      productIds: prev.productIds.includes(id) 
        ? prev.productIds.filter(p => p !== id) 
        : [...prev.productIds, id]
    }));
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Flash Sale Engine</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">High-Velocity Scarcity Marketing</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-orange-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-orange-100 hover:bg-orange-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Schedule Flash Sale
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {sales.length === 0 ? (
              <div className="bg-white p-20 rounded-[40px] border border-dashed border-gray-200 text-center space-y-4 opacity-50">
                 <Timer className="h-16 w-16 mx-auto text-gray-300" />
                 <p className="font-black uppercase tracking-widest text-xs">No flash sales active or scheduled. Create one to spike your traffic!</p>
              </div>
            ) : (
              sales.sort((a,b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()).map((sale) => (
                <div key={sale.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 hover:border-orange-200 transition-all group">
                   <div className="flex items-center gap-6">
                      <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center">
                         <Zap className="h-8 w-8 fill-current" />
                      </div>
                      <div>
                         <div className="flex items-center gap-3">
                            <h3 className="text-xl font-black text-gray-900">{sale.name}</h3>
                            <span className="bg-orange-100 text-orange-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">{sale.discountPercentage}% OFF</span>
                         </div>
                         <div className="flex items-center gap-4 mt-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(sale.startTime).toLocaleDateString()}</span>
                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {new Date(sale.startTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} - {new Date(sale.endTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                         </div>
                         <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase tracking-widest">{sale.productIds.length} Products Included</p>
                      </div>
                   </div>
                   <button 
                     onClick={() => { flashSaleService.deleteSale(sale.id); loadData(); }}
                     className="p-4 bg-gray-50 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all"
                   >
                      <Trash2 className="h-5 w-5" />
                   </button>
                </div>
              ))
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-orange-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Zap className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">FOMO Intelligence</h3>
                  <p className="text-orange-100 text-sm leading-relaxed font-medium">Limited-time deals create "Decision Urgency", increasing conversion rates by up to 45%.</p>
                  <div className="space-y-4 pt-4 border-t border-white/20">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><Check className="h-4 w-4" /></div>
                        <p className="text-xs font-bold uppercase tracking-widest">Dynamic Price Striking</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><Check className="h-4 w-4" /></div>
                        <p className="text-xs font-bold uppercase tracking-widest">Real-time Countdowns</p>
                     </div>
                  </div>
               </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 flex items-start gap-4">
               <Sparkles className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
               <p className="text-[10px] text-blue-700 font-medium leading-relaxed uppercase tracking-wider">
                  Pro-Tip: Run flash sales during peak browsing hours (7 PM - 10 PM) for maximum impact.
               </p>
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">New Flash Sale Event</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Event Name</label>
                       <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Midnight Tech Craze" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Discount (%)</label>
                       <input type="number" required value={formData.discountPercentage} onChange={e => setFormData({...formData, discountPercentage: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Starts At</label>
                       <input type="datetime-local" required value={formData.startTime} onChange={e => setFormData({...formData, startTime: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ends At</label>
                       <input type="datetime-local" required value={formData.endTime} onChange={e => setFormData({...formData, endTime: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>

                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Sale Inventory</label>
                    <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto pr-2">
                       {products.map(p => (
                         <button 
                           key={p.id}
                           type="button"
                           onClick={() => toggleProduct(p.id)}
                           className={`p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${formData.productIds.includes(p.id) ? 'border-orange-600 bg-orange-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                         >
                            <div className="flex items-center gap-3">
                               <img src={p.images[0]} className="w-8 h-8 rounded-lg object-cover" alt="" />
                               <span className="text-sm font-bold text-gray-700">{p.name}</span>
                            </div>
                            {formData.productIds.includes(p.id) && <Check className="h-4 w-4 text-orange-600" />}
                         </button>
                       ))}
                    </div>
                 </div>

                 <button 
                   type="submit" 
                   className="w-full py-6 bg-orange-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-orange-700 transition-all flex items-center justify-center gap-2"
                 >
                    Launch Campaign <Zap className="h-5 w-5 fill-current" />
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminFlashSales;
